import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import type { LibraryResponses } from "@tfg/types/endpoints/library"

import { useFindTraceabilityGraphByProductId } from "~/gen/api"
import { useFindFeatureFlagByName } from "~/hooks/useFindFeatureFlagByName"
import {
  filterOperationsByIds,
  getCustomFieldForkBranches,
  type CustomFieldForkBranch,
} from "~/pages/Brand/Products/Details/CustomFieldGraph/custom-field-fork-branches"
import type { GetItemOperations } from "~/pages/Brand/Products/Details/CustomFieldGraph/custom-field-graph.types"
import { MULTISOURCE_FEATURE_FLAG_NAME } from "~/pages/SupplierPortal/TraceabilityRequests/constants/multisource.constants"
import { getMainBranchOperationIdsByItemId } from "~/pages/Brand/Products/Details/utils"
import { useGetOperations } from "~/utils/operations"

type UseCustomFieldGraphDataResult = {
  readonly getMainOperations: GetItemOperations
  readonly forkBranches: readonly CustomFieldForkBranch[]
}

export function useCustomFieldGraphData(
  product: LibraryResponses.ProductDetails
): UseCustomFieldGraphDataResult {
  const { t: tTraceability } = useTranslation("traceability")
  const operationsByName = useGetOperations()
  const featureFlagQuery = useFindFeatureFlagByName(MULTISOURCE_FEATURE_FLAG_NAME)
  const isMultisourceEnabled = featureFlagQuery.data?.data?.state === true
  const graphQuery = useFindTraceabilityGraphByProductId(product.uuid, {
    query: {
      enabled: featureFlagQuery.isSuccess && isMultisourceEnabled,
    },
  })

  const mainBranchOperationIdsByItemId = getMainBranchOperationIdsByItemId(product)

  const getMainOperations: GetItemOperations = (item) => {
    const mainBranchOperationIds = mainBranchOperationIdsByItemId.get(item.id)
    if (mainBranchOperationIds === undefined) {
      return item.operations
    }
    return filterOperationsByIds(item.operations, mainBranchOperationIds)
  }

  const forkBranches = useMemo(
    () =>
      getCustomFieldForkBranches({
        nodes: graphQuery.data?.data?.nodes ?? [],
        translateItemType: (type) => tTraceability(`itemType.${type}`),
        translateActivity: (activityName) =>
          operationsByName[activityName]?.name ?? activityName,
      }),
    [graphQuery.data, tTraceability, operationsByName]
  )

  return { getMainOperations, forkBranches }
}
